import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import _indexOf from 'lodash/indexOf'

import { Command } from '@/shared/commands/command.types'

import { CELL_SELECT_COLORS } from '~shared/consts'
import { EMPTY_ARRAY } from '~shared/core/primitives'
import { ColumnType } from '~shared/rundown/column'
import type { CueId } from '~shared/rundown/cue'
import { getContentForSelectCell } from '~shared/types/Cell'

import { parseCellKey } from '@/features/rundown/cell/cell.schema'
import { useCellSnapshot } from '@/features/rundown/data/cells.store'
import { gridSaveActions, saveKeys } from '@/features/rundown/grid/grid.save'
import {
  useCellSelectSaveState,
  useColumnSaveState,
} from '@/features/rundown/grid/grid.save.hooks'
import { gridSelectionActions } from '@/features/rundown/grid/grid.store'

import { CellCommandContext, cellCommands } from '../../cell.commands'
import { useCellKey } from '../../cell.context'
import { useCellColumn, useCellIsReadOnly } from '../../cell.hooks'
import { CellFrame } from '../../CellFrame'
import { CellToolbar } from '../../toolbar/CellToolbar'

import {
  SelectColumnCellButton,
  SelectColumnCellContentWrapper,
} from './SelectColumnCell.shared'

const toolbarLocalCommands = [
  cellCommands.edit,
  cellCommands.history,
] satisfies Command<CellCommandContext>[]

export function SelectColumnCellPreview() {
  const cellKey = useCellKey()
  const { cueId } = parseCellKey(cellKey)

  const cell = useCellSnapshot()

  const column = useCellColumn()

  // Assert that this `Column` is of the `type` that has options
  if (column.type !== ColumnType.SELECT) {
    throw new Error('Invalid column data')
  }

  const isReadOnly = useCellIsReadOnly()

  const projectionOptions = column.options || EMPTY_ARRAY
  const optimisticOptions = useColumnSaveState(column.id, projectionOptions)
  const selectOptions = optimisticOptions.displayValue

  const projectionSelected = getContentForSelectCell(cell?.content)
  const optimisticSelected = useCellSelectSaveState(
    cellKey,
    projectionSelected,
    cell?.updatedAt,
  )
  const selectedValue = optimisticSelected.displayValue

  const selectedValueColor =
    CELL_SELECT_COLORS[_indexOf(selectOptions, selectedValue)]

  const isValidSelection = Boolean(
    selectedValue && selectOptions.includes(selectedValue),
  )
  const isOptionsEmpty = !selectOptions.length

  const handleUpdateSelected = (newSelectedValue: string) => {
    const content = selectedValue === newSelectedValue ? '' : newSelectedValue

    gridSaveActions.commit(saveKeys.cell(cellKey), {
      kind: 'cellSelect',
      cellKey: cellKey,
      content: {
        selected: content,
      },
    })
  }

  return (
    <CellFrame>
      <SelectColumnCellContentWrapper>
        <DropdownMenu disabled={isReadOnly}>
          <DropdownMenuTrigger
            render={
              <SelectColumnCellButton
                selectedValue={isValidSelection ? selectedValue : '-'}
                selectedValueColor={selectedValueColor}
                onDoubleClick={(e) => e.stopPropagation()} // Prevent triggering edit for multiple clicks
              />
            }
          />
          <DropdownMenuContent
            finalFocus={() => {
              // Return focus to parent cell when item is selected or menu is dismissed
              gridSelectionActions.selectCell(cueId as CueId, column.id, {
                focus: true,
              })
            }}
            className="w-auto min-w-(--anchor-width)"
          >
            {isOptionsEmpty ? (
              <DropdownMenuItem
                key="empty"
                disabled={true}
              >
                No options yet.
              </DropdownMenuItem>
            ) : (
              <DropdownMenuRadioGroup
                value={isValidSelection ? selectedValue : '-'}
                onValueChange={handleUpdateSelected}
              >
                {selectOptions.map((option, i) => (
                  <DropdownMenuRadioItem
                    key={option}
                    value={option}
                  >
                    <div
                      className="size-3.5 rounded-sm mr-1 bg-secondary"
                      style={{
                        backgroundColor: CELL_SELECT_COLORS[i],
                      }}
                    />
                    {option}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SelectColumnCellContentWrapper>

      <CellToolbar localCommands={toolbarLocalCommands} />
    </CellFrame>
  )
}
